import { createAsyncThunk } from "@reduxjs/toolkit";
import apiCall from "../AuthorizedApi";
import { showErrorToast, showSuccessToast } from "../../../components/atoms/Utlis/Toast";

export interface CreateTeamMemberPaymentPayload {
  team_member_id: string;
  amount: string;
  payment_method: string;
  payment_date: string;
  payment_type: string;
  transaction_reference?: string;
  description?: string;
  notes?: string;
  status: string;
}

export const createManualTeamMemberPayment = async (
  payload: CreateTeamMemberPaymentPayload
): Promise<unknown | null> => {
  try {
    const formData = new FormData();
    formData.append("team_member_id", payload.team_member_id);
    formData.append("amount", payload.amount);
    formData.append("payment_method", payload.payment_method);
    formData.append("payment_date", payload.payment_date);
    formData.append("payment_type", payload.payment_type);
    if (payload.transaction_reference)
      formData.append("transaction_reference", payload.transaction_reference);
    if (payload.description) formData.append("description", payload.description);
    if (payload.notes) formData.append("notes", payload.notes);
    formData.append("status", payload.status);

    const response = await apiCall({
      method: "POST",
      url: "/account/user/team-member-payment/",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
    });
    showSuccessToast("Payment recorded successfully.");
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; detail?: string } } };
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.detail ||
      "Failed to create payment. Please try again.";
    showErrorToast(message);
    return null;
  }
};

export interface TeamMemberPaymentFilterParams {
  team_member_id?: string | number;
  payment_type?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

function buildQueryString(params: TeamMemberPaymentFilterParams): string {
  const search = new URLSearchParams();
  if (params.team_member_id != null && params.team_member_id !== "")
    search.set("team_member_id", String(params.team_member_id));
  if (params.payment_type != null && params.payment_type !== "")
    search.set("payment_type", params.payment_type);
  if (params.start_date) search.set("start_date", params.start_date);
  if (params.end_date) search.set("end_date", params.end_date);
  if (params.page != null) search.set("page", String(params.page));
  if (params.page_size != null) search.set("page_size", String(params.page_size));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const getTeamMemberPaymentsAPI = createAsyncThunk(
  "teamMemberPayment/getTeamMemberPaymentsAPI",
  async (params: TeamMemberPaymentFilterParams | undefined, { rejectWithValue }) => {
    try {
      const query = buildQueryString(params ?? {});
      const response = await apiCall({
        method: "GET",
        url: `/account/user/team-member-payment/${query}`,
      });
      return response;
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string; detail?: string } } };
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to fetch team member payments.";
      if (status !== 403) {
        showErrorToast(message);
      }
      return rejectWithValue(err?.response?.data ? { ...err.response.data, status } : { message, status });
    }
  }
);

/** GET single payment by id */
export async function getTeamMemberPaymentById(
  paymentId: number | string
): Promise<import("../../../Data/teamMemberPayment.dto").TeamMemberPayment | null> {
  try {
    const response = await apiCall({
      method: "GET",
      url: `/account/user/team-member-payment/${paymentId}/`,
    });
    const res = response as { data?: unknown };
    const data = res?.data ?? response;
    return data as import("../../../Data/teamMemberPayment.dto").TeamMemberPayment;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    showErrorToast(err?.response?.data?.message ?? "Failed to fetch payment.");
    return null;
  }
}

export interface UpdateTeamMemberPaymentPayload {
  amount?: string;
  paid_amount?: string;
  description?: string;
  notes?: string;
}

/** PUT update payment by id (sends form-urlencoded; backend does not accept application/json). */
export const updateTeamMemberPayment = async (
  paymentId: number | string,
  payload: UpdateTeamMemberPaymentPayload
): Promise<unknown | null> => {
  try {
    const params = new URLSearchParams();
    if (payload.amount != null) params.append("amount", payload.amount);
    if (payload.paid_amount != null) params.append("paid_amount", payload.paid_amount);
    if (payload.description != null) params.append("description", payload.description);
    if (payload.notes != null) params.append("notes", payload.notes);

    const response = await apiCall({
      method: "PUT",
      url: `/account/user/team-member-payment/${paymentId}/`,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      data: params.toString(),
    });
    showSuccessToast("Payment updated successfully.");
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; detail?: string } } };
    showErrorToast(
      err?.response?.data?.message ??
        err?.response?.data?.detail ??
        "Failed to update payment."
    );
    return null;
  }
};

/** DELETE payment by id */
export const deleteTeamMemberPayment = async (
  paymentId: number | string
): Promise<unknown | null> => {
  try {
    const response = await apiCall({
      method: "DELETE",
      url: `/account/user/team-member-payment/${paymentId}/`,
    });
    showSuccessToast("Payment deleted successfully.");
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; detail?: string } } };
    showErrorToast(
      err?.response?.data?.message ??
        err?.response?.data?.detail ??
        "Failed to delete payment."
    );
    return null;
  }
};
